import { ComponentType } from '@angular/cdk/portal';
import { Directive, HostListener, Input, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { Client } from '@df/core/service/client';
import { ModalService } from '@df/ui/modal/modal.service';
import last from 'lodash-es/last';
import { Subscription } from 'rxjs';
import { ModalConfig, TModalPosition } from '@df/ui/modal/modal-config';
import { ModalRef } from '@df/ui/modal/modal-ref';

@Directive()
export abstract class AbstractModalOpenDirective<T, D = any> {
    constructor(
        protected modalService: ModalService,
        protected router: Router,
        protected client: Client
    ) {}

    protected routerSubscriber?: Subscription;

    abstract component: ComponentType<T> | TemplateRef<T>;

    protected modalRef?: ModalRef<T>;

    /**
     * Close this modal on navigation change.
     */
    @Input()
    closeOnNavigation?: boolean;

    /**
     * Close all opened modals before open this.
     */
    @Input()
    closeOther?: boolean;

    /**
     * Close all opened modals except those with the specified IDs.
     */
    @Input()
    closeOtherExcept?: string[];

    /**
     * Modal data
     */
    @Input()
    modalData!: D;

    /**
     * Modal config.
     */
    @Input()
    modalConfig: Partial<ModalConfig<D>> = {};

    @Input()
    modalPosition?: TModalPosition;

    @HostListener('click')
    onClick() {
        const config = {
            minWidth: '100%',
            height: '100vh',
            ...this.getModalConfig()
        };

        const lastModal: ModalRef<any> | undefined = last(this.modalService.openModals);

        if (this.closeOtherExcept?.length) {
            this.modalService.closeAllExcept(this.closeOtherExcept);
            this.openModal(config);
        } else if (this.closeOther && lastModal) {
            const lastModal$ = lastModal.afterClosed().subscribe(() => {
                this.openModal(config);

                lastModal$.unsubscribe();
            });

            this.modalService.closeAll();
        } else {
            this.openModal(config);
        }
    }

    protected openModal(config: ModalConfig<D>) {
        this.modalRef = this.modalService.open(
            this.component,
            {
                ...config,
                data: this.getModalData(),
                closeOnNavigation: this.closeOnNavigation
            },
            this.modalPosition
        );
    }

    protected getModalData(): D {
        return this.modalData;
    }

    protected getModalConfig(): Partial<ModalConfig<D>> {
        return this.modalConfig;
    }
}
